// Imports => Vendor
import axios from "axios";

// Imports => MOBX
import { makeObservable, observable, computed, action } from "mobx";

// Imports => Constants
import { KEYS } from "@opp/ui-kit";

// Imports => Utilities
import {
  AcSaveState,
  AcRemoveState,
  AcIsSet,
  AcIsNull,
  AcIsUndefined,
  AcFormatErrorMessage,
  AcFormatErrorCode,
  AcIsObject,
} from "@opp/ui-kit";
import { AcAutoLoad, AcAutoSave } from "@utils";

const _default = {
  settings: null,
};

let app = {};

export class SettingsStore {
  constructor(store) {
    makeObservable(this);

    AcAutoSave(this);

    app.store = store;
  }

  @observable
  settings = _default.settings;

  @computed
  get current_settings() {
    return this.settings;
  }

  @computed
  get compliance_overview_url() {
    if (
        !this.settings ||
        !AcIsObject(this.settings) ||
        !AcIsSet(this.settings.compliance_overview_url)
    )
      return false;
    return this.settings.compliance_overview_url;
  }

  @computed
  get configurations() {
    if (
        !this.settings ||
        !AcIsObject(this.settings) ||
        !AcIsSet(this.settings.configurations) ||
        !AcIsSet(this.settings.configurations[0])
    )
      return false;
    return this.settings.configurations[0];
  }

  @computed
  get has_continuous_settlement_interval() {
    if (
        !this.settings ||
        !AcIsObject(this.settings) ||
        !AcIsSet(this.settings.settlement_interval)
    )
      return false;
    return this.settings.settlement_interval === "continuous";
  }

  @computed
  get has_unverified_compliance_requirement() {
    if (
        !this.settings ||
        !AcIsObject(this.settings) ||
        !AcIsSet(this.settings.has_unverified_compliance_requirement)
    )
      return false;
    return this.settings.has_unverified_compliance_requirement;
  }

  @computed
  get has_pending_compliance_requirement() {
    if (
        !this.settings ||
        !AcIsObject(this.settings) ||
        !AcIsSet(this.settings.has_pending_compliance_requirement)
    )
      return false;
    return this.settings.has_pending_compliance_requirement;
  }

  @computed
  get has_open_tickets() {
    if (
        !this.settings ||
        !AcIsObject(this.settings) ||
        !AcIsSet(this.settings.has_open_tickets)
    )
      return false;
    return this.settings.has_open_tickets;
  }

  @computed
  get has_virtual_iban() {
    if (
        !this.settings ||
        !AcIsObject(this.settings) ||
        !AcIsSet(this.settings.has_virtual_iban)
    )
      return false;
    return this.settings.has_virtual_iban;
  }

  @computed
  get has_purchases() {
    if (
        !this.settings ||
        !AcIsObject(this.settings) ||
        !AcIsSet(this.settings.has_purchases)
    )
      return false;
    return this.settings.has_purchases;
  }

  @computed
  get has_dac7_report() {
    if (
        !this.settings ||
        !AcIsObject(this.settings) ||
        !AcIsSet(this.settings.has_dac7_report)
    )
      return false;
    return this.settings.has_dac7_report;
  }

  @computed
  get change_bank_account_url() {
    if (
        !this.settings ||
        !AcIsObject(this.settings) ||
        !AcIsSet(this.settings.change_bank_account_url)
    )
      return false;
    return this.settings.change_bank_account_url;
  }

  @observable
  loading = {
    status: false,
    message: null,
  };

  @computed
  get is_loading() {
    return this.loading.status;
  }

  @action
  setLoading = (state, message) => {
    this.loading = {
      status: state || false,
      message,
    };
  };

  @observable
  busy = {
    status: false,
    message: null,
  };

  @computed
  get is_busy() {
    return this.busy.status;
  }

  @action
  setBusy = (state, message) => {
    this.busy = {
      status: state || false,
      message,
    };
  };

  @action
  get = (t, params) => {
    this.setLoading(true);

    return app.store.api.settings
        .get(params)
        .then(async (response) => {
          await this.set(KEYS.SETTINGS, response);

          this.setLoading(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            app.store.toasters.add({
              variant: "error",
              title: t("settings.failed-to-retrieve", {ns: "store"}),
              description: AcFormatErrorMessage(error),
              code: AcFormatErrorCode(error),
            });
          }

          this.setLoading(false);

          if (!axios.isCancel(error)) throw error;
        });
  };

  @action
  set = (target, value, save = false) => {
    if (!AcIsSet(target)) return;
    if (AcIsUndefined(this[target])) return;
    if (AcIsUndefined(value)) return;

    return new Promise((resolve) => {
      this[target] = value;
      if (save) AcSaveState(target, value);
      resolve();
    });
  };

  @action
  setState = (target, property, value, save = false) => {
    if (!AcIsSet(target)) return;
    if (AcIsUndefined(this[target])) return;
    if (!AcIsSet(property)) return;
    if (AcIsUndefined(value)) return;

    this[target][property] = value;
    if (save) AcSaveState(target, value);
  };

  @action
  reset = (target, save = false) => {
    if (!AcIsSet(target)) return;
    if (AcIsUndefined(this[target])) return;

    return new Promise((resolve) => {
      this[target] = _default[target];
      if (save && AcIsNull(_default[target])) {
        AcRemoveState(target);
      } else if (save) {
        AcSaveState(target, _default[target]);
      }

      resolve();
    });
  };
}
export default SettingsStore;
